/**
 * Bóvedas, gastos y GNV de un turno (diálogo de la liquidación).
 *
 * Porta la gestión que el panel anterior abría en tres modales independientes
 * (`obtener_bovedas_turno` + `crear/editar/inhabilitar_boveda`,
 * `obtener_gastos_turno` + `crear/editar/inhabilitar_gasto` y
 * `guardar/eliminar_gnv_turno`): ingresos a bóveda, gastos del turno y la venta de
 * GNV. Las bóvedas y los gastos se descuentan del efectivo esperado, así que al
 * guardar se refresca el cuadre del día; el GNV se informa aparte, porque tampoco
 * entraba en el cuadre del módulo anterior.
 *
 * Las acciones se muestran según los permisos `liquidaciones.bovedas.*`,
 * `liquidaciones.gastos.*` y, para el GNV, el turno abierto.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  type BovedaDelTurno,
  type GastoDelTurno,
  type PermisosLiquidacion,
  type TurnoConCuadreOpcional,
} from '../reportes.api';

type Seccion = 'bovedas' | 'gastos' | 'gnv';

const SECCIONES: { valor: Seccion; etiqueta: string }[] = [
  { valor: 'bovedas', etiqueta: 'Bóvedas' },
  { valor: 'gastos', etiqueta: 'Gastos' },
  { valor: 'gnv', etiqueta: 'GNV' },
];

interface Props {
  /** Turno abierto o cerrado; con el cierre a ciegas llega **sin** `resumen_financiero`. */
  turno: TurnoConCuadreOpcional | null;
  permisos?: PermisosLiquidacion;
  onClose: () => void;
  /** Refresca la liquidación del día (los totales cambian al guardar). */
  onCambio: () => Promise<unknown> | void;
}

export function TurnoAjustesDialog({ turno, permisos, onClose, onCambio }: Props) {
  const cliente = useQueryClient();
  const [seccion, setSeccion] = useState<Seccion>('bovedas');
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // Formulario de bóveda
  const [bovedaTotal, setBovedaTotal] = useState('');
  const [bovedaObservacion, setBovedaObservacion] = useState('');
  const [bovedaEditando, setBovedaEditando] = useState<BovedaDelTurno | null>(null);

  // Formulario de gasto
  const [gastoCantidad, setGastoCantidad] = useState('');
  const [gastoDescripcion, setGastoDescripcion] = useState('');
  const [gastoTipo, setGastoTipo] = useState('');
  const [gastoEditando, setGastoEditando] = useState<GastoDelTurno | null>(null);

  // Formulario de GNV
  const [gnvMonto, setGnvMonto] = useState('');
  const [gnvVolumen, setGnvVolumen] = useState('');

  // Clave de un solo envío de la bóveda/gasto en curso (ver `newClaveDeEnvio`): se mantiene
  // hasta que el movimiento se confirma, para que un reintento no registre el dinero dos veces.
  const clavesDeEnvio = useRef<{ boveda: string | null; gasto: string | null }>({
    boveda: null,
    gasto: null,
  });

  const abierto = turno !== null;
  const turnoId = turno?.id ?? 0;

  const bovedas = useQuery({
    queryKey: ['reportes', 'turnos', 'bovedas', turnoId],
    queryFn: ({ signal }) => reportesApi.bovedasDelTurno(turnoId, signal),
    enabled: abierto,
  });

  const gastos = useQuery({
    queryKey: ['reportes', 'turnos', 'gastos', turnoId],
    queryFn: ({ signal }) => reportesApi.gastosDelTurno(turnoId, signal),
    enabled: abierto,
  });

  const gnv = useQuery({
    queryKey: ['reportes', 'turnos', 'gnv', turnoId],
    queryFn: ({ signal }) => reportesApi.gnvDelTurno(turnoId, signal),
    enabled: abierto,
  });

  /** Ejecuta una escritura, refresca las listas y la liquidación del día. */
  const ejecutar = async (accion: () => Promise<unknown>) => {
    setAviso(null);
    setOcupado(true);

    try {
      await accion();
      await cliente.invalidateQueries({ queryKey: ['reportes', 'turnos', 'bovedas', turnoId] });
      await cliente.invalidateQueries({ queryKey: ['reportes', 'turnos', 'gastos', turnoId] });
      await cliente.invalidateQueries({ queryKey: ['reportes', 'turnos', 'gnv', turnoId] });
      await onCambio();
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo guardar el cambio.');
    } finally {
      setOcupado(false);
    }
  };

  const limpiarBoveda = () => {
    setBovedaEditando(null);
    setBovedaTotal('');
    setBovedaObservacion('');
    clavesDeEnvio.current.boveda = null;
  };

  const limpiarGasto = () => {
    setGastoEditando(null);
    setGastoCantidad('');
    setGastoDescripcion('');
    setGastoTipo('');
    clavesDeEnvio.current.gasto = null;
  };

  const guardarBoveda = () => {
    const total = Number(bovedaTotal || 0);

    if (!total) {
      setAviso('Indica el importe del ingreso a bóveda.');
      return;
    }

    if (bovedaEditando) {
      void ejecutar(async () => {
        await reportesApi.editarBoveda(bovedaEditando.id, {
          total,
          observacion: bovedaObservacion,
        });
        limpiarBoveda();
      });
      return;
    }

    void ejecutar(async () => {
      const clave = (clavesDeEnvio.current.boveda ??= nuevaClaveDeEnvio());

      await reportesApi.crearBoveda({
        turno_id: turnoId,
        total,
        observacion: bovedaObservacion,
        clave_idempotencia: clave,
      });
      limpiarBoveda();
    });
  };

  const guardarGasto = () => {
    const cantidad = Number(gastoCantidad || 0);

    if (!cantidad) {
      setAviso('Indica el importe del gasto.');
      return;
    }

    if (gastoEditando) {
      void ejecutar(async () => {
        await reportesApi.editarGasto(gastoEditando.id, {
          cantidad,
          descripcion: gastoDescripcion,
          tipo: gastoTipo,
        });
        limpiarGasto();
      });
      return;
    }

    void ejecutar(async () => {
      const clave = (clavesDeEnvio.current.gasto ??= nuevaClaveDeEnvio());

      await reportesApi.crearGasto({
        turno_id: turnoId,
        cantidad,
        descripcion: gastoDescripcion,
        tipo: gastoTipo,
        clave_idempotencia: clave,
      });
      limpiarGasto();
    });
  };

  const guardarGnv = () => {
    void ejecutar(async () => {
      await reportesApi.guardarGnv({
        turno_id: turnoId,
        monto_gnv: Number(gnvMonto || 0),
        volumen_m3: Number(gnvVolumen || 0),
      });
      setGnvMonto('');
      setGnvVolumen('');
    });
  };

  const puedeBoveda = permisos?.bovedas_crear;
  const puedeBovedaEditar = permisos?.bovedas_editar;
  const puedeBovedaEliminar = permisos?.bovedas_eliminar;
  const puedeGasto = permisos?.gastos_crear;
  const puedeGastoEditar = permisos?.gastos_editar;
  const puedeGastoEliminar = permisos?.gastos_eliminar;

  const totalBovedas = (bovedas.data ?? [])
    .filter((boveda) => Number(boveda.estado) === 1)
    .reduce((total, boveda) => total + Number(boveda.total ?? 0), 0);

  const totalGastos = (gastos.data ?? [])
    .filter((gasto) => Number(gasto.estado) === 1)
    .reduce((total, gasto) => total + Number(gasto.cantidad ?? 0), 0);

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      title={seccion === 'gnv' ? 'Ventas GNV del turno' : 'Bóvedas y gastos del turno'}
      description={
        turno
          ? `Turno #${turno.id} · ${turno.first_name ?? ''} ${turno.last_name ?? ''}`.trim()
          : undefined
      }
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {SECCIONES.map((opcion) => (
            <button
              key={opcion.valor}
              type="button"
              aria-pressed={seccion === opcion.valor}
              onClick={() => {
                setSeccion(opcion.valor);
                setAviso(null);
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                seccion === opcion.valor
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
              )}
            >
              {opcion.etiqueta}
              {opcion.valor === 'bovedas' && totalBovedas > 0
                ? ` · ${formatCurrency(totalBovedas)}`
                : ''}
              {opcion.valor === 'gastos' && totalGastos > 0 ? ` · ${formatCurrency(totalGastos)}` : ''}
            </button>
          ))}

          {/* Con **cierre a ciegas** el turno llega sin `resumen_financiero` (la API lo omite
              para que el operador no pueda calcular la venta): el cuadre no se muestra. */}
          {turno?.resumen_financiero ? (
            <span className="ml-auto text-xs text-ink-500">
              Cuadre del turno: bóvedas{' '}
              {formatCurrency(turno.resumen_financiero.total_bovedas ?? 0)} · gastos{' '}
              {formatCurrency(turno.resumen_financiero.total_gastos ?? 0)}
            </span>
          ) : null}
        </div>

        {aviso ? <Alert variant="warning" title="Revisa el dato">{aviso}</Alert> : null}

        {bovedas.isError || gastos.isError || gnv.isError ? (
          <Alert variant="error" title="No se pudieron cargar los datos del turno">
            {bovedas.error instanceof ApiError
              ? bovedas.error.message
              : gastos.error instanceof ApiError
                ? gastos.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
          </Alert>
        ) : null}

        {seccion === 'bovedas' ? (
          <div className="space-y-3">
            {puedeBoveda || bovedaEditando ? (
              <div className="grid items-end gap-3 rounded-card border border-ink-100 p-3 sm:grid-cols-[9rem_1fr_auto]">
                <Input
                  label="Importe"
                  type="number"
                  step="0.01"
                  value={bovedaTotal}
                  onChange={(evento) => setBovedaTotal(evento.target.value)}
                />
                <Input
                  label="Observación"
                  value={bovedaObservacion}
                  onChange={(evento) => setBovedaObservacion(evento.target.value)}
                  placeholder="Número de sobre, responsable, etc."
                />
                <div className="flex gap-2">
                  <Button loading={ocupado} onClick={guardarBoveda} leftIcon={<Check className="size-4" aria-hidden="true" />}>
                    {bovedaEditando ? 'Guardar' : 'Registrar'}
                  </Button>
                  {bovedaEditando ? (
                    <Button variant="outline" onClick={limpiarBoveda}>
                      Cancelar
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {bovedas.isLoading ? <Skeleton className="h-24 w-full" /> : null}

            {bovedas.data ? (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fecha</th>
                    <th className="px-2 py-2">Observación</th>
                    <th className="px-2 py-2 text-right">Importe</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bovedas.data.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={5} className="px-2 py-6 text-center text-sm text-ink-500">
                        El turno no tiene ingresos a bóveda.
                      </td>
                    </tr>
                  ) : null}

                  {bovedas.data.map((boveda) => (
                    <tr
                      key={boveda.id}
                      className={cn(
                        'border-t border-ink-100',
                        Number(boveda.estado) !== 1 && 'text-ink-400 line-through',
                      )}
                    >
                      <td className="px-2 py-2 text-ink-600">
                        {formatDateValue(boveda.fecha)}
                        {boveda.fecha ? ` ${new Date(boveda.fecha).toTimeString().slice(0, 5)}` : ''}
                      </td>
                      <td className="px-2 py-2 text-ink-700">{boveda.observacion ?? '—'}</td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(Number(boveda.total ?? 0))}
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant={Number(boveda.estado) === 1 ? 'success' : 'neutral'}>
                          {Number(boveda.estado) === 1 ? 'Activa' : 'Inhabilitada'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1.5">
                          {puedeBovedaEditar && Number(boveda.estado) === 1 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setBovedaEditando(boveda);
                                setBovedaTotal(String(Number(boveda.total ?? 0)));
                                setBovedaObservacion(boveda.observacion ?? '');
                              }}
                              leftIcon={<Pencil className="size-3.5" aria-hidden="true" />}
                            >
                              Editar
                            </Button>
                          ) : null}

                          {puedeBovedaEliminar && Number(boveda.estado) === 1 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              loading={ocupado}
                              onClick={() => void ejecutar(() => reportesApi.inhabilitarBoveda(boveda.id))}
                              leftIcon={<Trash2 className="size-3.5" aria-hidden="true" />}
                            >
                              Inhabilitar
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {bovedas.data.length > 0 ? (
                  <tfoot>
                    <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={2}>
                        Total activo
                      </td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totalBovedas)}</td>
                      <td className="px-2 py-2" colSpan={2} />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            ) : null}
          </div>
        ) : null}

        {seccion === 'gastos' ? (
          <div className="space-y-3">
            {puedeGasto || gastoEditando ? (
              <div className="grid items-end gap-3 rounded-card border border-ink-100 p-3 sm:grid-cols-[9rem_1fr_9rem_auto]">
                <Input
                  label="Importe"
                  type="number"
                  step="0.01"
                  value={gastoCantidad}
                  onChange={(evento) => setGastoCantidad(evento.target.value)}
                />
                <Input
                  label="Descripción"
                  value={gastoDescripcion}
                  onChange={(evento) => setGastoDescripcion(evento.target.value)}
                />
                <Input
                  label="Tipo"
                  value={gastoTipo}
                  onChange={(evento) => setGastoTipo(evento.target.value)}
                  placeholder="Movilidad, limpieza…"
                />
                <div className="flex gap-2">
                  <Button loading={ocupado} onClick={guardarGasto} leftIcon={<Check className="size-4" aria-hidden="true" />}>
                    {gastoEditando ? 'Guardar' : 'Registrar'}
                  </Button>
                  {gastoEditando ? (
                    <Button variant="outline" onClick={limpiarGasto}>
                      Cancelar
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {gastos.isLoading ? <Skeleton className="h-24 w-full" /> : null}

            {gastos.data ? (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fecha</th>
                    <th className="px-2 py-2">Descripción</th>
                    <th className="px-2 py-2">Tipo</th>
                    <th className="px-2 py-2 text-right">Importe</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {gastos.data.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-2 py-6 text-center text-sm text-ink-500">
                        El turno no tiene gastos registrados.
                      </td>
                    </tr>
                  ) : null}

                  {gastos.data.map((gasto) => (
                    <tr
                      key={gasto.id}
                      className={cn(
                        'border-t border-ink-100',
                        Number(gasto.estado) !== 1 && 'text-ink-400 line-through',
                      )}
                    >
                      <td className="px-2 py-2 text-ink-600">{formatDateValue(gasto.fecha_gasto)}</td>
                      <td className="px-2 py-2 text-ink-700">{gasto.descripcion ?? '—'}</td>
                      <td className="px-2 py-2 text-ink-600">{gasto.tipo_gasto ?? '—'}</td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(Number(gasto.cantidad ?? 0))}
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant={Number(gasto.estado) === 1 ? 'success' : 'neutral'}>
                          {Number(gasto.estado) === 1 ? 'Activo' : 'Inhabilitado'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1.5">
                          {puedeGastoEditar && Number(gasto.estado) === 1 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setGastoEditando(gasto);
                                setGastoCantidad(String(Number(gasto.cantidad ?? 0)));
                                setGastoDescripcion(gasto.descripcion ?? '');
                                setGastoTipo(gasto.tipo_gasto ?? '');
                              }}
                              leftIcon={<Pencil className="size-3.5" aria-hidden="true" />}
                            >
                              Editar
                            </Button>
                          ) : null}

                          {puedeGastoEliminar && Number(gasto.estado) === 1 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              loading={ocupado}
                              onClick={() => void ejecutar(() => reportesApi.inhabilitarGasto(gasto.id))}
                              leftIcon={<Trash2 className="size-3.5" aria-hidden="true" />}
                            >
                              Inhabilitar
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {gastos.data.length > 0 ? (
                  <tfoot>
                    <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={3}>
                        Total activo
                      </td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totalGastos)}</td>
                      <td className="px-2 py-2" colSpan={2} />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            ) : null}
          </div>
        ) : null}

        {seccion === 'gnv' ? (
          <div className="space-y-3">
            <div className="grid items-end gap-3 rounded-card border border-ink-100 p-3 sm:grid-cols-[10rem_10rem_auto]">
              <Input
                label="Monto GNV (S/)"
                type="number"
                step="0.01"
                value={gnvMonto}
                onChange={(evento) => setGnvMonto(evento.target.value)}
              />
              <Input
                label="Volumen (m³)"
                type="number"
                step="0.001"
                value={gnvVolumen}
                onChange={(evento) => setGnvVolumen(evento.target.value)}
              />
              <Button
                loading={ocupado}
                onClick={guardarGnv}
                leftIcon={<Plus className="size-4" aria-hidden="true" />}
              >
                {Number(turno?.monto_gnv ?? 0) > 0 ? 'Actualizar' : 'Registrar'}
              </Button>
            </div>

            {gnv.isLoading ? <Skeleton className="h-20 w-full" /> : null}

            {gnv.data && gnv.data.length > 0 ? (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Monto</th>
                    <th className="px-2 py-2">Volumen (m³)</th>
                    <th className="px-2 py-2">Equivalente</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {gnv.data.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100">
                      <td className="px-2 py-2 font-semibold text-ink-900">
                        {formatCurrency(Number(fila.monto_gnv ?? 0))}
                      </td>
                      <td className="px-2 py-2 text-ink-600">
                        {formatNumber(Number(fila.volumen_m3 ?? 0))}
                      </td>
                      <td className="px-2 py-2 text-ink-500">
                        {formatNumber(Number(fila.volumen_m3 ?? 0) / 3.1)} gal
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          loading={ocupado}
                          onClick={() => void ejecutar(() => reportesApi.eliminarGnv(fila.id))}
                          leftIcon={<Trash2 className="size-3.5" aria-hidden="true" />}
                        >
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            <p className="text-xs text-ink-500">
              Venta de GNV registrada en el turno: {formatCurrency(Number(turno?.monto_gnv ?? 0))} (
              {formatNumber(Number(turno?.volumen_gnv ?? 0))} m³ ={' '}
              {formatNumber(Number(turno?.volumen_gnv ?? 0) / 3.1)} gal). Se informa en el ticket y en
              el parte diario, y no entra en el cuadre de la caja, igual que en el módulo anterior.
            </p>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
